import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearStoredAdminToken,
  createAdminCategory,
  createAdminProduct,
  deleteAdminProduct,
  getAdminMe,
  getAdminProduct,
  getStoredAdminToken,
  listAdminProducts,
  updateAdminProduct,
  uploadAdminImage,
} from '../services/adminAuthApi';
import { getCategories, resolveMediaUrl } from '../services/productsApi';
import AdminAttributeCatalog from './AdminAttributeCatalog';
import AdminProductAttributesSection from './AdminProductAttributesSection';

const emptyForm = {
  categoryId: '',
  name: '',
  description: '',
  dimensions: '',
  materials: '',
  imageCover: '',
  imageAmbient: '',
  imageVariant1: '',
  imageVariant2: '',
  imageVariant3: '',
  imageVariant4: '',
};

const VARIANT_IMAGE_FIELDS = [
  { key: 'imageVariant1', label: 'Variante 1', position: 3 },
  { key: 'imageVariant2', label: 'Variante 2', position: 4 },
  { key: 'imageVariant3', label: 'Variante 3', position: 5 },
  { key: 'imageVariant4', label: 'Variante 4', position: 6 },
];

const NEW_PRODUCT_DRAFT_KEY = 'verissa-admin-new-product-draft';

const hasDraftContent = (form, assignments) => {
  const formHasValue = Object.values(form || {}).some((value) =>
    String(value || '').trim()
  );
  return formHasValue || (Array.isArray(assignments) && assignments.length > 0);
};

const readNewProductDraft = () => {
  try {
    const raw = window.localStorage.getItem(NEW_PRODUCT_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.formData || typeof parsed.formData !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeNewProductDraft = (formData, assignments) => {
  try {
    window.localStorage.setItem(
      NEW_PRODUCT_DRAFT_KEY,
      JSON.stringify({
        formData,
        assignments,
        savedAt: Date.now(),
      })
    );
  } catch {
    // localStorage lleno o bloqueado
  }
};

const clearNewProductDraft = () => {
  try {
    window.localStorage.removeItem(NEW_PRODUCT_DRAFT_KEY);
  } catch {
    // ignore
  }
};

const AdminPanel = () => {
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');
  const [uploadingField, setUploadingField] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isProductListExpanded, setIsProductListExpanded] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [attributeDefinitions, setAttributeDefinitions] = useState([]);
  const [productAttributeAssignments, setProductAttributeAssignments] = useState([]);
  const [draftReady, setDraftReady] = useState(false);
  const [draftNotice, setDraftNotice] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const editFromHashHandled = useRef(false);

  const loadProducts = useCallback(async () => {
    const token = getStoredAdminToken();
    if (!token) return;
    setListLoading(true);
    try {
      const list = await listAdminProducts(token);
      setProducts(Array.isArray(list) ? list : []);
    } catch {
      setProducts([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      const token = getStoredAdminToken();
      if (!token) {
        window.location.hash = 'admin-login';
        return;
      }

      try {
        const [me, categoriesResponse] = await Promise.all([
          getAdminMe(token),
          getCategories(),
        ]);
        if (mounted) {
          setAdmin(me);
          setCategories(categoriesResponse || []);
        }
        await loadProducts();
      } catch (err) {
        clearStoredAdminToken();
        if (mounted) {
          setError('Tu sesión expiró. Vuelve a iniciar sesión.');
        }
        window.location.hash = 'admin-login';
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSession();
    return () => {
      mounted = false;
    };
  }, [loadProducts]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const categoryOptions = useMemo(() => {
    const byId = categories.reduce((acc, category) => {
      acc[String(category.id)] = category;
      return acc;
    }, {});
    const roots = categories.filter((category) => !category.parent_id);
    const children = categories.filter((category) => category.parent_id);
    const options = [];

    roots.forEach((root) => {
      options.push({
        id: root.id,
        label: root.name,
      });
      children
        .filter((child) => String(child.parent_id) === String(root.id))
        .forEach((child) => {
          options.push({
            id: child.id,
            label: `${root.name} › ${child.name}`,
          });
        });
    });

    children.forEach((child) => {
      if (options.some((option) => String(option.id) === String(child.id))) {
        return;
      }
      const parent = byId[String(child.parent_id)];
      options.push({
        id: child.id,
        label: parent ? `${parent.name} › ${child.name}` : child.name,
      });
    });

    return options;
  }, [categories]);

  const rootCategoryOptions = useMemo(
    () => categories.filter((category) => !category.parent_id),
    [categories]
  );

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const token = getStoredAdminToken();
    const name = newCategoryName.trim();
    if (!token || !name) return;

    setIsSavingCategory(true);
    setStatusMessage('');
    try {
      const created = await createAdminCategory(token, {
        name,
        parentId: newCategoryParentId || null,
      });
      const refreshed = await getCategories();
      setCategories(refreshed || []);
      setFormData((prev) => ({
        ...prev,
        categoryId: String(created.id),
      }));
      setNewCategoryName('');
      setNewCategoryParentId('');
      setStatusType('success');
      setStatusMessage(`Categoría “${created.name}” creada y seleccionada.`);
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo crear la categoría.');
    } finally {
      setIsSavingCategory(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    const draft = readNewProductDraft();
    if (hasDraftContent(draft?.formData, draft?.assignments)) {
      setFormData({ ...emptyForm, ...draft.formData });
      setProductAttributeAssignments(
        Array.isArray(draft.assignments) ? draft.assignments : []
      );
      setDraftNotice('Se restauró el borrador del producto nuevo.');
    } else {
      setFormData({ ...emptyForm });
      setProductAttributeAssignments([]);
      setDraftNotice('');
    }
    setStatusMessage('');
    setStatusType('');
  };

  const discardNewProductDraft = () => {
    clearNewProductDraft();
    setFormData({ ...emptyForm });
    setProductAttributeAssignments([]);
    setDraftNotice('');
    setStatusType('success');
    setStatusMessage('Borrador descartado.');
  };

  const handleFileChange = async (event, targetField) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = getStoredAdminToken();
    if (!token) {
      setStatusType('error');
      setStatusMessage('Sesión expirada. Inicia sesión nuevamente.');
      window.location.hash = 'admin-login';
      return;
    }

    setUploadingField(targetField);
    setStatusMessage('');

    try {
      const result = await uploadAdminImage(token, file);
      setFormData((prev) => ({
        ...prev,
        [targetField]: result.path || '',
      }));
      setStatusType('success');
      setStatusMessage('Imagen subida correctamente.');
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo subir la imagen.');
    } finally {
      setUploadingField(null);
      event.target.value = '';
    }
  };

  const handleClearImage = (targetField) => {
    setFormData((prev) => ({
      ...prev,
      [targetField]: '',
    }));
  };

  const handleUploadOptionImage = async (file) => {
    const token = getStoredAdminToken();
    if (!token) {
      setStatusType('error');
      setStatusMessage('Sesión expirada. Inicia sesión nuevamente.');
      window.location.hash = 'admin-login';
      throw new Error('Sesión expirada');
    }

    const result = await uploadAdminImage(token, file);
    return result.path || '';
  };

  const renderImageField = (fieldKey, label, { optional = false } = {}) => {
    const inputId = `admin-${fieldKey}`;
    const imagePath = formData[fieldKey];
    const isUploading = uploadingField === fieldKey;

    return (
      <div className="form-row">
        <label htmlFor={inputId}>
          {label}
          {optional ? ' (opcional)' : ''}
        </label>
        <div className="admin-file-input">
          <div className="admin-file-picker">
            <label
              className={`admin-file-btn ${
                isUploading || isSaving ? 'disabled' : ''
              }`}
              htmlFor={inputId}
            >
              {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, fieldKey)}
              disabled={isUploading || isSaving}
            />
          </div>
          <span className="admin-file-text">
            {imagePath ? imagePath : 'Ningún archivo seleccionado'}
          </span>
          {imagePath && (
            <button
              type="button"
              className="admin-file-remove-btn"
              onClick={() => handleClearImage(fieldKey)}
              disabled={isUploading || isSaving}
            >
              Quitar
            </button>
          )}
        </div>
      </div>
    );
  };

  const buildImagesPayload = () => {
    const imagesPayload = [];
    if (formData.imageCover.trim()) {
      imagesPayload.push({
        type: 'cover',
        path: formData.imageCover.trim(),
        alt: `${formData.name} - producto`,
        position: 1,
      });
    }
    if (formData.imageAmbient.trim()) {
      imagesPayload.push({
        type: 'ambient',
        path: formData.imageAmbient.trim(),
        alt: `${formData.name} - ambientada`,
        position: 2,
      });
    }
    VARIANT_IMAGE_FIELDS.forEach(({ key, label, position }) => {
      const path = formData[key]?.trim();
      if (path) {
        imagesPayload.push({
          type: 'variant',
          path,
          alt: `${formData.name} - ${label.toLowerCase()}`,
          position,
        });
      }
    });
    return imagesPayload;
  };

  const handleSaveProduct = async (event) => {
    event.preventDefault();
    const token = getStoredAdminToken();
    if (!token) {
      setStatusType('error');
      setStatusMessage('Sesión expirada. Inicia sesión nuevamente.');
      window.location.hash = 'admin-login';
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    setStatusType('');

    try {
      const imagesPayload = buildImagesPayload();

      if (editingId) {
        await updateAdminProduct(token, editingId, {
          categoryId: Number(formData.categoryId),
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          dimensions: formData.dimensions.trim() || null,
          materials: formData.materials.trim() || null,
          images: imagesPayload,
          attributeAssignments: productAttributeAssignments,
        });
        setStatusType('success');
        setStatusMessage('Producto actualizado correctamente.');
        cancelEdit();
      } else {
        await createAdminProduct(token, {
          categoryId: Number(formData.categoryId),
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          dimensions: formData.dimensions.trim() || null,
          materials: formData.materials.trim() || null,
          images: imagesPayload,
          attributeAssignments: productAttributeAssignments,
        });
        clearNewProductDraft();
        setDraftNotice('');
        setStatusType('success');
        setStatusMessage('Producto creado correctamente.');
        setFormData({ ...emptyForm });
        setProductAttributeAssignments([]);
      }
      await loadProducts();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(
        err.message ||
          (editingId
            ? 'No se pudo actualizar el producto.'
            : 'No se pudo crear el producto.')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = useCallback(async (row) => {
    const token = getStoredAdminToken();
    if (!token || !row?.id) return;
    setStatusMessage('');
    try {
      const detail = await getAdminProduct(token, row.id);
      const cover = detail.images?.find((i) => i.image_type === 'cover');
      const ambient = detail.images?.find((i) => i.image_type === 'ambient');
      const variants = (detail.images || [])
        .filter((i) => i.image_type === 'variant')
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setEditingId(detail.id);
      setFormData({
        categoryId: String(detail.category_id),
        name: detail.name || '',
        description: detail.description || '',
        dimensions: detail.dimensions || '',
        materials: detail.materials || '',
        imageCover: cover?.image_path || '',
        imageAmbient: ambient?.image_path || '',
        imageVariant1: variants[0]?.image_path || '',
        imageVariant2: variants[1]?.image_path || '',
        imageVariant3: variants[2]?.image_path || '',
        imageVariant4: variants[3]?.image_path || '',
      });
      setProductAttributeAssignments(
        Array.isArray(detail.attribute_assignments)
          ? detail.attribute_assignments
          : []
      );
      window.scrollTo({ top: 220, behavior: 'smooth' });
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo cargar el producto.');
    }
  }, []);

  useEffect(() => {
    if (loading || !admin || editFromHashHandled.current) return;

    const raw = window.location.hash.slice(1) || '';
    const query = raw.includes('?') ? raw.split('?')[1] : '';
    const editId = Number(new URLSearchParams(query).get('edit'));
    if (!Number.isFinite(editId) || editId <= 0) return;

    editFromHashHandled.current = true;
    startEdit({ id: editId });
  }, [loading, admin, startEdit]);

  useEffect(() => {
    if (loading || !admin || draftReady) return;

    const raw = window.location.hash.slice(1) || '';
    const query = raw.includes('?') ? raw.split('?')[1] : '';
    const hasHashEdit = Boolean(new URLSearchParams(query).get('edit'));

    if (!hasHashEdit) {
      const draft = readNewProductDraft();
      if (hasDraftContent(draft?.formData, draft?.assignments)) {
        setFormData({ ...emptyForm, ...draft.formData });
        setProductAttributeAssignments(
          Array.isArray(draft.assignments) ? draft.assignments : []
        );
        setDraftNotice('Se restauró un borrador del alta de producto.');
      }
    }

    setDraftReady(true);
  }, [loading, admin, draftReady]);

  useEffect(() => {
    if (!draftReady || editingId) return;

    const raw = window.location.hash.slice(1) || '';
    const query = raw.includes('?') ? raw.split('?')[1] : '';
    if (new URLSearchParams(query).get('edit')) return;

    if (!hasDraftContent(formData, productAttributeAssignments)) {
      clearNewProductDraft();
      return;
    }
    writeNewProductDraft(formData, productAttributeAssignments);
  }, [draftReady, editingId, formData, productAttributeAssignments]);

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `¿Eliminar "${row.name}" del catálogo? Dejará de mostrarse en la tienda.`
      )
    ) {
      return;
    }
    const token = getStoredAdminToken();
    if (!token) return;
    try {
      await deleteAdminProduct(token, row.id);
      if (editingId === row.id) {
        cancelEdit();
      }
      setStatusType('success');
      setStatusMessage('Producto eliminado del catálogo.');
      await loadProducts();
    } catch (err) {
      setStatusType('error');
      setStatusMessage(err.message || 'No se pudo eliminar el producto.');
    }
  };

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) => {
      const haystack = [
        product.name,
        product.category_name,
        product.slug,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [products, productSearch]);

  if (loading) {
    return (
      <section className="admin-auth-page">
        <div className="admin-auth-card">
          <p>Validando sesión...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-auth-page">
      <div className="admin-auth-card admin-panel-card">
        <h1>Panel Administrador</h1>
        {admin ? (
          <p>
            Sesión activa como <strong>{admin.email}</strong>.
          </p>
        ) : (
          <p>{error || 'No autorizado.'}</p>
        )}

        <div className="admin-product-list-section">
          <button
            type="button"
            className="admin-product-list-toggle"
            onClick={() => setIsProductListExpanded((v) => !v)}
            aria-expanded={isProductListExpanded}
            aria-controls="admin-product-list-panel"
            id="admin-product-list-heading"
          >
            <span className="admin-product-list-toggle-text">
              <span className="admin-panel-section-title admin-product-list-title-inline">
                Productos en catálogo
              </span>
              {!listLoading && products.length > 0 && (
                <span className="admin-product-list-count">
                  {productSearch.trim()
                    ? `${filteredProducts.length} de ${products.length}`
                    : products.length}{' '}
                  {products.length === 1 ? 'producto' : 'productos'}
                </span>
              )}
            </span>
            <span
              className={`admin-product-list-chevron ${
                isProductListExpanded ? 'expanded' : ''
              }`}
              aria-hidden="true"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
          {isProductListExpanded && (
            <div
              id="admin-product-list-panel"
              role="region"
              aria-labelledby="admin-product-list-heading"
            >
              <p className="admin-panel-hint">
                Edita o elimina productos existentes. Eliminar solo los oculta en la
                web (no borra el registro de la base).
              </p>
              <div className="admin-product-search">
                <label htmlFor="admin-product-search">Buscar producto</label>
                <div className="admin-product-search-row">
                  <input
                    id="admin-product-search"
                    type="search"
                    value={productSearch}
                    onChange={(event) => setProductSearch(event.target.value)}
                    placeholder="Nombre o categoría..."
                    autoComplete="off"
                  />
                  {productSearch.trim() && (
                    <button
                      type="button"
                      className="admin-product-search-clear"
                      onClick={() => setProductSearch('')}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
              <div className="admin-product-table-wrap">
                {listLoading ? (
                  <p className="admin-panel-hint">Cargando lista...</p>
                ) : products.length === 0 ? (
                  <p className="admin-panel-hint">Aún no hay productos registrados.</p>
                ) : filteredProducts.length === 0 ? (
                  <p className="admin-panel-hint">
                    No hay productos que coincidan con “{productSearch.trim()}”.
                  </p>
                ) : (
                  <table className="admin-product-table">
                    <thead>
                      <tr>
                        <th>Vista</th>
                        <th>Nombre</th>
                        <th>Categoría</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((p) => (
                        <tr
                          key={p.id}
                          className={!p.is_active ? 'admin-product-row-inactive' : ''}
                        >
                          <td className="admin-product-thumb-cell">
                            {p.cover_image ? (
                              <img
                                src={resolveMediaUrl(p.cover_image)}
                                alt=""
                                className="admin-product-thumb"
                              />
                            ) : (
                              <span className="admin-panel-hint">—</span>
                            )}
                          </td>
                          <td>{p.name}</td>
                          <td>{p.category_name}</td>
                          <td>
                            {p.is_active ? (
                              <span className="admin-badge admin-badge-active">Activo</span>
                            ) : (
                              <span className="admin-badge admin-badge-inactive">
                                Inactivo
                              </span>
                            )}
                          </td>
                          <td className="admin-product-actions">
                            <button
                              type="button"
                              className="admin-table-btn"
                              onClick={() => startEdit(p)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="admin-table-btn danger"
                              onClick={() => handleDelete(p)}
                              disabled={!p.is_active}
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        <AdminAttributeCatalog onCatalogChange={setAttributeDefinitions} />

        <h2 className="admin-panel-section-title">
          {editingId ? 'Editar producto' : 'Nuevo producto'}
        </h2>
        {!editingId &&
          hasDraftContent(formData, productAttributeAssignments) && (
          <div className="admin-edit-banner admin-draft-banner">
            <span>
              {draftNotice ||
                'Este alta se guarda sola como borrador. Si se recarga o sales del panel, puedes continuar aquí.'}
            </span>
            <div className="admin-edit-banner-actions">
              <button
                type="button"
                className="admin-auth-btn secondary admin-cancel-edit-btn"
                onClick={discardNewProductDraft}
                disabled={isSaving}
              >
                Descartar borrador
              </button>
            </div>
          </div>
        )}
        {editingId && (
          <div className="admin-edit-banner">
            <span>Estás editando un producto existente. Pulsa Guardar para aplicar en la base de datos.</span>
            <div className="admin-edit-banner-actions">
              <button
                type="button"
                className="admin-auth-btn secondary admin-cancel-edit-btn"
                onClick={cancelEdit}
                disabled={isSaving}
              >
                Cancelar edición
              </button>
              <button
                type="submit"
                form="admin-product-form"
                className="admin-auth-btn"
                disabled={isSaving}
              >
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        )}

        <form
          id="admin-product-form"
          className="admin-panel-form"
          onSubmit={handleSaveProduct}
        >
          <div className="form-row">
            <label htmlFor="admin-category">Categoría *</label>
            <select
              id="admin-category"
              name="categoryId"
              required
              value={formData.categoryId}
              onChange={handleChange}
            >
              <option value="">Selecciona categoría</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-category-create">
            <p className="admin-panel-hint">
              ¿No está la categoría? Agrégala aquí y se guarda en la base de
              datos.
            </p>
            <div className="admin-category-create-row">
              <input
                id="admin-new-category-name"
                type="text"
                value={newCategoryName}
                onChange={(event) => setNewCategoryName(event.target.value)}
                placeholder="Nombre de la nueva categoría"
                aria-label="Nombre de la nueva categoría"
              />
              <select
                id="admin-new-category-parent"
                value={newCategoryParentId}
                onChange={(event) => setNewCategoryParentId(event.target.value)}
                aria-label="Categoría padre opcional"
              >
                <option value="">Sin categoría padre</option>
                {rootCategoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    Subcategoría de {category.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="admin-auth-btn secondary"
                onClick={handleCreateCategory}
                disabled={isSavingCategory || !newCategoryName.trim()}
              >
                {isSavingCategory ? 'Guardando...' : 'Agregar categoría'}
              </button>
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="admin-name">Nombre *</label>
            <input
              id="admin-name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Nombre del producto"
            />
          </div>

          <div className="form-row">
            <label htmlFor="admin-description">Descripción</label>
            <textarea
              id="admin-description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              placeholder="Descripción corta del producto"
            />
          </div>

          <div className="admin-panel-grid">
            <div className="form-row">
              <label htmlFor="admin-dimensions">Dimensiones</label>
              <input
                id="admin-dimensions"
                name="dimensions"
                type="text"
                value={formData.dimensions}
                onChange={handleChange}
                placeholder="1.80 x 0.80 mts."
              />
            </div>
            <div className="form-row">
              <label htmlFor="admin-materials">Materiales</label>
              <input
                id="admin-materials"
                name="materials"
                type="text"
                value={formData.materials}
                onChange={handleChange}
                placeholder="Melamina."
              />
            </div>
          </div>

          <div className="admin-panel-grid">
            {renderImageField('imageCover', 'Imagen principal')}
            {renderImageField('imageAmbient', 'Imagen ambientada', {
              optional: true,
            })}
          </div>

          <p className="admin-panel-hint admin-images-section-hint">
            Imágenes de variantes (opcionales). Se muestran como miniaturas en la ficha del producto.
          </p>
          <div className="admin-panel-grid">
            {VARIANT_IMAGE_FIELDS.map(({ key, label }) =>
              renderImageField(key, label, { optional: true })
            )}
          </div>

          <AdminProductAttributesSection
            attributeDefinitions={attributeDefinitions}
            assignments={productAttributeAssignments}
            onChange={setProductAttributeAssignments}
            onUploadOptionImage={handleUploadOptionImage}
            disabled={isSaving}
          />

          <button type="submit" className="admin-auth-btn" disabled={isSaving}>
            {isSaving
              ? 'Guardando...'
              : editingId
                ? 'Guardar cambios'
                : 'Guardar producto'}
          </button>
        </form>
        {statusMessage && (
          <p className={`admin-auth-status ${statusType}`}>{statusMessage}</p>
        )}
        <div className="admin-auth-actions">
          <button
            type="button"
            className="admin-auth-btn secondary"
            onClick={() => {
              clearStoredAdminToken();
              window.location.hash = 'admin-login';
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </section>
  );
};

export default AdminPanel;
